import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import * as projectsApi from "./projects.api";
import { IUseProjects } from "./projects.hooks.interface";
import { projectFormSchema } from "./projects.schema";
import { TCreateProjectRequest, TProjectFormData } from "./projects.types";

export const useProjects = (clientId?: string): IUseProjects => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const projectForm = useForm<TProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: "",
      type_client: "affinity",
      level_hierarchical: undefined,
      user_commercials: [],
      partner_participants: [],
      hunters: [],
    },
  });

  // Mutation para criar projeto
  const createProjectMutation = useMutation({
    mutationFn: ({ data, organizationId }: { data: TCreateProjectRequest; organizationId: string }) => 
      projectsApi.createProject(data, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: t("generic.success"),
        description: "Projeto criado com sucesso!",
      });
      navigate(`/clients`, {
        state: { openClientId: clientId },
      });
    },
    onError: (error: any) => {
      // Tentar extrair mensagem de erro mais detalhada
      let errorMessage = "Erro ao criar projeto";
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Se for um array de mensagens, juntar todas
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage.join(", ");
      }
      
      console.error("Erro ao criar projeto:", error);
      
      toast({
        title: t("generic.error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = React.useCallback(
    (data: TProjectFormData) => {
      console.log("handleSubmit chamado com dados:", data);
      
      if (!clientId) {
        toast({
          title: t("generic.error"),
          description: "ID do cliente não encontrado",
          variant: "destructive",
        });
        return;
      }

      // Preparar dados para a API com a nova estrutura
      const requestData: TCreateProjectRequest = {
        organization_father_id: clientId,
        project_name: data.name,
        type_client: data.type_client,
      };

      // Adicionar hierarchical_level apenas se for affiliate
      // O backend exige que seja um número inteiro quando type_client é "affiliate"
      // IMPORTANTE: O backend espera "hierarchical_level" (não "level_hierarchical")
      if (data.type_client === "affiliate") {
        const hierarchicalLevel = data.level_hierarchical;
        if (hierarchicalLevel !== undefined && hierarchicalLevel !== null && !isNaN(Number(hierarchicalLevel))) {
          // Garantir que seja um número inteiro válido
          const levelInt = Math.floor(Number(hierarchicalLevel));
          requestData.hierarchical_level = levelInt > 0 ? levelInt : 1;
        } else {
          requestData.hierarchical_level = 1; // Valor padrão se não fornecido ou inválido
        }
      }

      // Adicionar usuários comerciais se existirem
      // Multiplicar por 100 para enviar ao backend (ex: 10.5% vira 1050)
      if (data.user_commercials && data.user_commercials.length > 0) {
        requestData.user_commercials = data.user_commercials.map((user) => ({
          id: user.id,
          commission_percentage: Math.round(user.commission_percentage * 100),
        }));
      }

      // Adicionar parceiros participantes se existirem
      if (data.partner_participants && data.partner_participants.length > 0) {
        requestData.partner_particpants = data.partner_participants.map((partner) => ({
          id: partner.id,
        }));
      }

      // Adicionar hunters se existirem
      if (data.hunters && data.hunters.length > 0) {
        requestData.hunters = data.hunters.map((hunter) => ({
          id: hunter.id,
        }));
      }

      console.log("Dados enviados para a API:", requestData);

      // Criar projeto
      createProjectMutation.mutate({ 
        data: requestData, 
        organizationId: clientId 
      });
    },
    [clientId, createProjectMutation, toast, t]
  );

  return {
    projectForm,
    handleSubmit,
    isCreating: createProjectMutation.isPending,
  };
};


